import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Store,
  Search,
  MapPin,
  Map as MapIcon,
  Trash2,
  Plus,
  X,
  Calendar,
  Edit,
  Grid3x3,
  List,
  Table as TableIcon,
  RefreshCw,
  Settings,
  Info,
  Loader2
} from 'lucide-react';
import api from '../../services/api';
import { cn } from '@/utils/cn';
import { useAlert } from '../../components/AlertProvider';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ColumnConfigurator, type ColumnConfig } from '@/components/ui/ColumnConfigurator';

interface ClientInfo {
  id: number;
  company_name: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  status: string | null;
}

interface ClientContact {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  member_type: string | null;
  is_primary: boolean | null;
  is_active: boolean | null;
}

interface SalesPoint {
  id: number;
  client_id: number;
  name: string;
  code: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  maps_link: string | null;
  phone: string | null;
  email: string | null;
  manager_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  notes: string | null;
  recommended_time: string | null;
  client?: ClientInfo | null;
}

const TIME_SLOTS = [
  '08h - 09h', '09h - 10h', '10h - 11h', '11h - 12h',
  '12h - 13h', '13h - 14h', '14h - 15h', '15h - 16h',
  '16h - 17h', '17h - 18h', '18h - 19h', '19h - 20h'
];

type SalesPointMapEntry = {
  item: SalesPoint;
  coords: { lat: number; lng: number } | null;
  addressLabel: string;
};

interface BuyerOption {
  id: number;
  company_name: string;
  first_name?: string | null;
  last_name?: string | null;
}

const SalesPointsTailwind: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError: showErrorAlert } = useAlert();

  // Storage keys for persistence
  const searchStorageKey = 'sales_points_table_search';
  const filterStorageKey = 'sales_points_table_filters';
  const columnStorageKey = 'sales_points_table_columns';

  // Default columns configuration
  const defaultColumns: ColumnConfig[] = [
    { key: 'name', label: 'Point de vente', visible: true },
    { key: 'contact', label: 'Contact', visible: true },
    { key: 'client_email', label: 'Email client', visible: true },
    { key: 'client', label: 'Client', visible: true },
    { key: 'address', label: 'Adresse', visible: true },
    { key: 'recommended_time', label: 'Horaire recommandé', visible: true },
    { key: 'status', label: 'Statut', visible: true },
    { key: 'actions', label: 'Actions', visible: true },
  ];

  const loadColumnsFromStorage = (): ColumnConfig[] => {
    try {
      const stored = localStorage.getItem(columnStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnConfig[];
        return parsed.length > 0 ? parsed : defaultColumns;
      }
    } catch (e) {
      console.error('Error loading column config:', e);
    }
    return defaultColumns;
  };

  const storedFilters = (() => {
    try {
      return JSON.parse(localStorage.getItem(filterStorageKey) || '{}') as {
        buyerFilter?: 'all' | number;
        onlyActive?: 'all' | boolean;
      };
    } catch (e) {
      console.error('Error parsing saved filters:', e);
      return {};
    }
  })();

  const buildAddressComplete = (address: string, postalCode: string, city: string) => {
    return [address, postalCode, city]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const getContactDisplayName = (contact: ClientContact): string => {
    const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    return name || contact.email || 'Contact';
  };

  const getPrimaryContact = (clientId?: number): ClientContact | null => {
    if (!clientId) return null;
    const contacts = clientContactsById[clientId];
    return contacts?.[0] ?? null;
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed` : '';
  };

  const extractLatLngFromMapsLink = (mapsLink?: string | null) => {
    if (!mapsLink) return null;
    const atMatch = mapsLink.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
    if (atMatch) {
      const lat = Number(atMatch[1]);
      const lng = Number(atMatch[2]);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return { lat, lng };
    }
    const queryMatch = mapsLink.match(/[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
    if (queryMatch) {
      const lat = Number(queryMatch[1]);
      const lng = Number(queryMatch[2]);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return { lat, lng };
    }
    return null;
  };

  const buildSalesPointAddressLabel = (salesPoint: SalesPoint): string => {
    const baseAddress = buildAddressComplete(
      salesPoint.address || '',
      salesPoint.postal_code || '',
      salesPoint.city || ''
    );
    return [baseAddress, salesPoint.country].filter(Boolean).join(', ');
  };

  // Initialiser le filtre client depuis l'URL
  const getInitialClientFilter = (): 'all' | number => {
    const clientIdParam = searchParams.get('client_id');
    if (clientIdParam) {
      const clientId = parseInt(clientIdParam, 10);
      if (!isNaN(clientId)) return clientId;
    }
    return 'all';
  };

  const [salesPoints, setSalesPoints] = useState<SalesPoint[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(200);
  const [mapItems, setMapItems] = useState<any[]>([]);
  const [missingCoordsItems, setMissingCoordsItems] = useState<any[]>([]);
  const [buyers, setBuyers] = useState<BuyerOption[]>([]);
  const [buyerFilter, setBuyerFilter] = useState<'all' | number>(() => {
    const urlFilter = getInitialClientFilter();
    return urlFilter !== 'all' ? urlFilter : (storedFilters.buyerFilter ?? 'all');
  });
  const [onlyActive, setOnlyActive] = useState<'all' | boolean>(storedFilters.onlyActive ?? 'all');
  const [search, setSearch] = useState<string>(() => localStorage.getItem(searchStorageKey) || '');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table' | 'map'>(() => window.innerWidth < 768 ? 'grid' : 'table');
  const [listMapPreviewPointId, setListMapPreviewPointId] = useState<number | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [viewModal, setViewModal] = useState<SalesPoint | null>(null);
  const [editModal, setEditModal] = useState<SalesPoint | null>(null);
  const [editForm, setEditForm] = useState<Partial<SalesPoint>>({});
  const [showEditMapPreview, setShowEditMapPreview] = useState<boolean>(false);
  const [createModal, setCreateModal] = useState<boolean>(false);
  const [showCreateMapPreview, setShowCreateMapPreview] = useState<boolean>(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; salesPoint: SalesPoint | null }>({ open: false, salesPoint: null });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [clientContactsById, setClientContactsById] = useState<Record<number, ClientContact[]>>({});
  const [clientContactsLoadingId, setClientContactsLoadingId] = useState<number | null>(null);
  const [columns, setColumns] = useState<ColumnConfig[]>(loadColumnsFromStorage);
  const [showColumnConfig, setShowColumnConfig] = useState<boolean>(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);
  const markersLayerRef = useRef<any>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapRefreshBusy, setMapRefreshBusy] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState<{ total: number; done: number; updated: number; failed: number } | null>(null);
  const shouldCenterNextRef = useRef(false);
  const isInternalMapMove = useRef(false);
  const lastBboxRef = useRef<string>('');
  const lastExtentRef = useRef<any>(null);
  const [createForm, setCreateForm] = useState<Partial<SalesPoint>>(() => {
    const clientIdParam = searchParams.get('client_id');
    const clientId = clientIdParam ? parseInt(clientIdParam, 10) : undefined;
    return {
      client_id: !isNaN(clientId as number) ? clientId : undefined,
      name: '',
      code: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'France',
      maps_link: '',
      phone: '',
      email: '',
      manager_name: '',
      is_active: true,
      notes: '',
      recommended_time: null
    };
  });

  // Charger la liste des clients pour affichage et filtres
  useEffect(() => {
    const loadBuyers = async (): Promise<void> => {
      try {
        const res = await api.get('/clients', { params: { limit: 500 } });
        const payload = res.data;
        const items = Array.isArray(payload) ? payload : (payload?.items ?? []);
        setBuyers(items.map((b: any) => ({
          id: b.id,
          company_name: b.company_name,
          first_name: b.first_name,
          last_name: b.last_name
        })));
      } catch (e) {
        console.error('Load buyers failed', e);
        setBuyers([]);
      }
    };

    void loadBuyers();
  }, []);

  // Persist search to localStorage
  useEffect(() => {
    localStorage.setItem(searchStorageKey, search);
  }, [search, searchStorageKey]);

  // Persist filters to localStorage
  useEffect(() => {
    localStorage.setItem(filterStorageKey, JSON.stringify({
      buyerFilter,
      onlyActive
    }));
  }, [buyerFilter, onlyActive, filterStorageKey]);

  // Appliquer le filtre client_id depuis l'URL
  useEffect(() => {
    const buyerIdParam = searchParams.get('client_id');
    if (buyerIdParam) {
      const clientId = parseInt(buyerIdParam, 10);
      if (!isNaN(clientId)) {
        setBuyerFilter(clientId);
        // Pré-remplir le client pour la création
        setCreateForm(prev => ({ ...prev, client_id: clientId }));
      }
    }
  }, [searchParams]);

  const loadSalesPoints = useCallback(async (bbox?: { minLat: number, maxLat: number, minLng: number, maxLng: number }) => {
    // If we have a bbox, check if it's the same as the last one to avoid infinite loops
    if (bbox) {
      const bboxString = `${bbox.minLat.toFixed(4)},${bbox.maxLat.toFixed(4)},${bbox.minLng.toFixed(4)},${bbox.maxLng.toFixed(4)}`;
      if (bboxString === lastBboxRef.current) return;
      lastBboxRef.current = bboxString;
    } else {
      // If no bbox, it's a "hard" refresh (filter change), we should center the map after loading
      lastBboxRef.current = '';
      shouldCenterNextRef.current = true;
    }

    setBusy(true);
    setError('');
    try {
      if (viewMode === 'map') {
        const params: Record<string, any> = {};
        if (search) params.search = search;
        if (buyerFilter !== 'all') params.client_id = buyerFilter;
        if (onlyActive !== 'all') params.only_active = onlyActive;

        if (bbox) {
          params.min_lat = bbox.minLat;
          params.max_lat = bbox.maxLat;
          params.min_lng = bbox.minLng;
          params.max_lng = bbox.maxLng;
        }

        const res = await api.get('/clients/sales-points/all/map', { params });
        setMapItems(res.data.items || []);

        // Only update missing coords items if we fetched everything (no bbox)
        if (!bbox) {
          const missing = (res.data.items || []).filter((item: any) => !item.lat || !item.lng);
          setMissingCoordsItems(missing);

          // Store extent for later if map not ready yet
          if (res.data.extent) {
            lastExtentRef.current = res.data.extent;
          }

          // Use the extent from backend to center the map if available
          if (res.data.extent && leafletMapRef.current && shouldCenterNextRef.current) {
            const { min_lat, max_lat, min_lng, max_lng } = res.data.extent;
            const bounds = (window as any).L.latLngBounds([min_lat, min_lng], [max_lat, max_lng]);

            leafletMapRef.current.fitBounds(bounds.pad(0.1), { animate: false });
            shouldCenterNextRef.current = false;

            // Manually trigger a bbox-based load after fitting bounds to ensure 
            // markers are fetched, as fitBounds with animate:false might not 
            // consistently trigger moveend on all Leaflet versions/browsers.
            // lastBboxRef.current will prevent duplicate loads if moveend DOES fire.
            const newBounds = leafletMapRef.current.getBounds();
            void loadSalesPoints({
              minLat: newBounds.getSouth(),
              maxLat: newBounds.getNorth(),
              minLng: newBounds.getWest(),
              maxLng: newBounds.getEast()
            });
          }
        }

        setBusy(false);
        return;
      }

      const params: Record<string, any> = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (buyerFilter !== 'all') params.client_id = buyerFilter;
      if (onlyActive !== 'all') params.only_active = onlyActive;
      if (search) params.search = search;

      const res = await api.get('/clients/sales-points', { params });
      const data = res.data;
      if (data && typeof data === 'object' && 'items' in data) {
        setSalesPoints(Array.isArray(data.items) ? data.items : []);
        setTotal(data.total || 0);
      } else {
        // Fallback for plain array response
        const arr = Array.isArray(data) ? data : [];
        setSalesPoints(arr);
        setTotal(arr.length);
      }
    } catch (e: any) {
      console.error('Load sales points failed:', e);
      setError(e?.response?.data?.detail || "Erreur lors du chargement des points de vente");
      setSalesPoints([]);
    } finally {
      setBusy(false);
    }
  }, [buyerFilter, onlyActive, search, viewMode, page, pageSize]);

  // Reset page when filters or search change
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
  }, [buyerFilter, onlyActive, search]);

  // Load data with debounce
  useEffect(() => {
    const t = setTimeout(() => {
      void loadSalesPoints();
    }, 300);
    return () => clearTimeout(t);
  }, [loadSalesPoints]);

  // Expose window functions for map popup actions
  useEffect(() => {
    (window as any).handleMapSalesPointClick = (id: number) => {
      const sp = salesPoints.find(p => p.id === id) || mapItems.find(p => p.id === id);
      if (sp) {
        handleEdit(sp as SalesPoint);
      }
    };
    return () => {
      delete (window as any).handleMapSalesPointClick;
    };
  }, [salesPoints, mapItems]);

  const filteredSalesPoints = useMemo(() => {
    if (!search.trim()) return salesPoints;
    const needle = search.toLowerCase();
    return salesPoints.filter((sp) => {
      return (
        sp.name.toLowerCase().includes(needle) ||
        (sp.code ?? '').toLowerCase().includes(needle) ||
        (sp.city ?? '').toLowerCase().includes(needle) ||
        (sp.manager_name ?? '').toLowerCase().includes(needle) ||
        (sp.client?.company_name ?? '').toLowerCase().includes(needle) ||
        (sp.client?.email ?? '').toLowerCase().includes(needle) ||
        `${sp.client?.first_name ?? ''} ${sp.client?.last_name ?? ''}`.trim().toLowerCase().includes(needle)
      );
    });
  }, [salesPoints, search]);

  const resetAllFilters = (): void => {
    setSearch('');
    localStorage.removeItem(searchStorageKey);
    localStorage.removeItem(filterStorageKey);
    setBuyerFilter('all');
    setOnlyActive('all');
    setPage(1);
  };

  const activeFilters = useMemo(() => {
    const tags: Array<{ key: string; label: string; onClear: () => void }> = [];
    if (search.trim()) tags.push({ key: 'search', label: `Recherche: ${search}`, onClear: () => setSearch('') });
    if (buyerFilter !== 'all') {
      const buyer = buyers.find(b => b.id === buyerFilter);
      tags.push({
        key: 'buyer',
        label: `Client: ${buyer?.company_name || buyerFilter}`,
        onClear: () => setBuyerFilter('all')
      });
    }
    if (onlyActive !== 'all') {
      tags.push({
        key: 'active',
        label: `Statut: ${onlyActive ? 'Actifs uniquement' : 'Inactifs uniquement'}`,
        onClear: () => setOnlyActive('all')
      });
    }
    return tags;
  }, [search, buyerFilter, onlyActive, buyers]);

  const visibleColumns = useMemo(() => columns.filter(col => col.visible), [columns]);

  const mapEntries = useMemo<SalesPointMapEntry[]>(() => {
    if (viewMode !== 'map') {
      return filteredSalesPoints.map((item) => ({
        item,
        coords: extractLatLngFromMapsLink(item.maps_link),
        addressLabel: buildSalesPointAddressLabel(item)
      }));
    }

    // In map mode, we combine mapItems (visible markers) and missingCoordsItems (sidebar)
    // to build the full map summary
    const combined = [...mapItems];

    // Add missing coords items if they aren't already in mapItems
    missingCoordsItems.forEach(item => {
      if (!combined.find(m => m.id === item.id)) {
        combined.push(item);
      }
    });

    return combined.map((item) => ({
      item: {
        ...item,
        client: { company_name: item.company_name }
      } as SalesPoint,
      coords: (item.lat && item.lng) ? { lat: item.lat, lng: item.lng } : null,
      addressLabel: [item.address, item.postal_code, item.city, item.country].filter(Boolean).join(', ')
    }));
  }, [filteredSalesPoints, mapItems, missingCoordsItems, viewMode]);

  const mapSummary = useMemo(() => {
    const withCoords = mapEntries.filter((entry) => entry.coords);
    const withoutCoords = mapEntries.filter((entry) => !entry.coords);
    const active = mapEntries.filter((entry) => entry.item.is_active).length;
    const inactive = mapEntries.filter((entry) => !entry.item.is_active).length;
    return {
      total: mapEntries.length,
      withCoords,
      withoutCoords,
      active,
      inactive
    };
  }, [mapEntries]);

  const isMapView = viewMode === 'map';
  const showLoadingState = !isMapView && busy;
  const showEmptyState = !isMapView && filteredSalesPoints.length === 0;

  useEffect(() => {
    if (viewMode !== 'map') return;

    let isMounted = true;
    const loadLeaflet = async () => {
      if (!mapRef.current || leafletMapRef.current) return;
      setMapLoading(true);
      try {
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }

        if (!document.querySelector('link[href*="MarkerCluster.css"]')) {
          const clusterLink = document.createElement('link');
          clusterLink.rel = 'stylesheet';
          clusterLink.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css';
          document.head.appendChild(clusterLink);
          const clusterDefaultLink = document.createElement('link');
          clusterDefaultLink.rel = 'stylesheet';
          clusterDefaultLink.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css';
          document.head.appendChild(clusterDefaultLink);
        }

        if (!(window as any).L) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!(window as any).L.markerClusterGroup) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!isMounted || !mapRef.current) return;

        const L = (window as any).L;
        const map = L.map(mapRef.current).setView([46.6, 2.5], 5.6);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        markersLayerRef.current = L.layerGroup().addTo(map);
        leafletMapRef.current = map;

        // Add listeners for moveend and zoomend to re-fetch data based on bbox with debouncing
        let moveTimeout: any;
        map.on('moveend zoomend', () => {
          if (isInternalMapMove.current) return;

          if (moveTimeout) clearTimeout(moveTimeout);
          moveTimeout = setTimeout(() => {
            if (isInternalMapMove.current) return;

            const bounds = map.getBounds();
            const bbox = {
              minLat: bounds.getSouth(),
              maxLat: bounds.getNorth(),
              minLng: bounds.getWest(),
              maxLng: bounds.getEast()
            };
            void loadSalesPoints(bbox);
          }, 400);
        });

        setMapReady(true);
        // Force a size recalculation after the map is in the DOM
        map.invalidateSize();
        // Delayed invalidateSize for mobile where container may not have rendered yet
        setTimeout(() => map.invalidateSize(), 100);
        setTimeout(() => map.invalidateSize(), 500);

        // If we already have an extent but haven't centered yet, do it now
        if (shouldCenterNextRef.current && lastExtentRef.current) {
          const { min_lat, max_lat, min_lng, max_lng } = lastExtentRef.current;
          const bounds = L.latLngBounds([min_lat, min_lng], [max_lat, max_lng]);
          map.fitBounds(bounds.pad(0.1), { animate: false });
          shouldCenterNextRef.current = false;

          // Trigger manual load for markers
          const newBounds = map.getBounds();
          void loadSalesPoints({
            minLat: newBounds.getSouth(),
            maxLat: newBounds.getNorth(),
            minLng: newBounds.getWest(),
            maxLng: newBounds.getEast()
          });
        } else if (shouldCenterNextRef.current) {
          // If no extent yet (maybe no points or load still pending), 
          // trigger an initial bbox load for the default view
          const bounds = map.getBounds();
          const bbox = {
            minLat: bounds.getSouth(),
            maxLat: bounds.getNorth(),
            minLng: bounds.getWest(),
            maxLng: bounds.getEast()
          };
          void loadSalesPoints(bbox);
        }
      } catch (error) {
        console.error('Failed to load map:', error);
        if (isMounted) setMapReady(false);
      } finally {
        if (isMounted) setMapLoading(false);
      }
    };

    void loadLeaflet();

    return () => {
      isMounted = false;
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      markersLayerRef.current = null;
      setMapReady(false);
    };
  }, [viewMode, loadSalesPoints]);

  useEffect(() => {
    if (viewMode !== 'map') return;
    if (!mapReady) return;
    const map = leafletMapRef.current;
    const L = (window as any).L;
    if (!map || !L) return;

    setMapLoading(true);

    if (!markersLayerRef.current) {
      markersLayerRef.current = L.layerGroup().addTo(map);
    }

    markersLayerRef.current.clearLayers();
    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      chunkedLoading: true,
      maxClusterRadius: 30,
      disableClusteringAtZoom: 8,
      spiderfyDistanceMultiplier: 1.5
    });

    const bounds = L.latLngBounds([]);
    const entriesWithCoords = mapItems.filter(item => item.lat && item.lng);

    const getClientStatusColor = (status: string | null): string => {
      switch (status) {
        case 'prospect': return '#9333ea'; // purple-600
        case 'actif': return '#10b981';    // emerald-500
        case 'inactif': return '#eab308';  // yellow-500
        case 'bloque': return '#ef4444';   // red-500
        case 'ferme': return '#71717a';    // zinc-500
        default: return '#3b82f6';         // blue-500
      }
    };

    const createColorIcon = (color: string) => {
      return L.divIcon({
        className: '',
        iconSize: [28, 40],
        iconAnchor: [14, 40],
        popupAnchor: [0, -36],
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
          <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.27 21.73 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
          <circle cx="14" cy="14" r="6" fill="#fff"/>
        </svg>`
      });
    };

    entriesWithCoords.forEach((item) => {
      const pinColor = getClientStatusColor(item.client_status);
      const marker = L.marker([item.lat, item.lng], { icon: createColorIcon(pinColor) });
      const popupContent = `
        <div style="min-width:180px" class="p-2">
          <div class="font-bold text-slate-900 mb-1">${item.name}</div>
          <div class="text-xs text-slate-500 mb-1">${item.company_name}</div>
          <div class="text-xs text-slate-500 mb-2">${item.address || item.city || ''}</div>
          <div class="flex items-center gap-2">
            <span class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${item.is_active
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200'}">
              ${item.is_active ? 'Actif' : 'Inactif'}
            </span>
          </div>
          <button 
            onclick="handleMapSalesPointClick(${item.id})"
            class="mt-3 w-full px-3 py-1.5 bg-blue-600 text-white text-[10px] font-bold rounded hover:bg-blue-700 transition-colors"
          >
            VOIR DÉTAILS
          </button>
        </div>
      `;
      marker.bindPopup(popupContent.trim());
      clusterGroup.addLayer(marker);
      bounds.extend([item.lat, item.lng]);
    });

    markersLayerRef.current.addLayer(clusterGroup);

    // Only fitBounds if shouldCenterNextRef.current is true (i.e. after a filter change, not a map move)
    if (shouldCenterNextRef.current) {
      shouldCenterNextRef.current = false;
      isInternalMapMove.current = true;

      if (entriesWithCoords.length > 0 || lastExtentRef.current) {
        const targetBounds = entriesWithCoords.length > 0
          ? bounds
          : L.latLngBounds([lastExtentRef.current.min_lat, lastExtentRef.current.min_lng], [lastExtentRef.current.max_lat, lastExtentRef.current.max_lng]);

        map.fitBounds(targetBounds.pad(0.1), { animate: false });
      } else {
        map.setView([46.6, 2.5], 5, { animate: false });
      }

      // Release the lock after a short delay AND trigger a refresh with the actual bbox
      setTimeout(() => {
        isInternalMapMove.current = false;
        const finalBounds = map.getBounds();
        const finalBbox = {
          minLat: finalBounds.getSouth(),
          maxLat: finalBounds.getNorth(),
          minLng: finalBounds.getWest(),
          maxLng: finalBounds.getEast()
        };
        void loadSalesPoints(finalBbox);
      }, 600);
    }

    requestAnimationFrame(() => setMapLoading(false));
  }, [mapItems, viewMode, mapReady]);

  const handleRefreshMapLinks = async (): Promise<void> => {
    const totalToProcess = mapSummary.withoutCoords.length;
    if (totalToProcess === 0) return;

    setMapRefreshBusy(true);
    setGeocodeProgress({ total: totalToProcess, done: 0, updated: 0, failed: 0 });

    const BATCH_SIZE = 10;
    let totalUpdated = 0;
    let totalFailed = 0;
    let totalDone = 0;
    let consecutiveEmpty = 0;

    try {
      while (totalDone < totalToProcess && consecutiveEmpty < 3) {
        const response = await api.post('/clients/sales-points/maps/refresh', {
          limit: BATCH_SIZE
        }, { timeout: 120000 });
        const data = response.data || {};
        const batchUpdated = data.updated ?? 0;
        const batchFailed = data.failed ?? 0;
        const batchProcessed = data.processed ?? 0;

        totalUpdated += batchUpdated;
        totalFailed += batchFailed;
        totalDone += batchProcessed;

        setGeocodeProgress({ total: totalToProcess, done: totalDone, updated: totalUpdated, failed: totalFailed });

        if (batchProcessed === 0 || (batchUpdated === 0 && batchFailed === 0)) {
          consecutiveEmpty++;
        } else {
          consecutiveEmpty = 0;
        }

        if (batchProcessed < BATCH_SIZE) break;
      }

      if (totalUpdated > 0) {
        showSuccess(`${totalUpdated} point(s) de vente géolocalisé(s) sur ${totalDone} traité(s)`);
      } else if (totalFailed > 0) {
        showErrorAlert(`Aucune adresse géolocalisée sur ${totalDone} traitée(s). Vérifiez les adresses.`);
      } else {
        showSuccess('Aucun point de vente à géolocaliser');
      }
      await loadSalesPoints();
    } catch (e) {
      showErrorAlert('Erreur lors de la mise à jour des coordonnées');
    } finally {
      setMapRefreshBusy(false);
      setTimeout(() => setGeocodeProgress(null), 3000);
    }
  };

  const renderTableHeader = (col: ColumnConfig): JSX.Element | null => {
    switch (col.key) {
      case 'name':
        return <th key={col.key} className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Point de vente</th>;
      case 'contact':
        return <th key={col.key} className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Contact</th>;
      case 'client_email':
        return <th key={col.key} className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Email client</th>;
      case 'client':
        return <th key={col.key} className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Client</th>;
      case 'address':
        return <th key={col.key} className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Adresse</th>;
      case 'recommended_time':
        return <th key={col.key} className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Horaire recommandé</th>;
      case 'status':
        return <th key={col.key} className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Statut</th>;
      case 'actions':
        return <th key={col.key} className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Actions</th>;
      default:
        return null;
    }
  };

  const renderTableCell = (col: ColumnConfig, item: SalesPoint): JSX.Element | null => {
    switch (col.key) {
      case 'name':
        return (
          <td key={col.key} className="px-4 py-3 align-top">
            <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">{item.name}</span>
          </td>
        );
      case 'contact':
        return (
          <td key={col.key} className="px-4 py-3 align-top">
            {item.client ? (
              (() => {
                const primaryContact = getPrimaryContact(item.client.id);
                const contactName = primaryContact ? getContactDisplayName(primaryContact) : `${item.client.first_name ?? ''} ${item.client.last_name ?? ''}`.trim();
                return (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/achat/clients/${item.client!.id}`);
                    }}
                    className="text-left text-sm font-semibold text-slate-900 dark:text-slate-100 hover:underline"
                  >
                    {contactName || 'Contact'}
                  </button>
                );
              })()
            ) : (
              <span className="text-sm text-slate-400 italic">Non renseigné</span>
            )}
          </td>
        );
      case 'client_email':
        return (
          <td key={col.key} className="px-4 py-3 align-top">
            {item.client?.email ? (
              <span className="text-sm text-slate-600 dark:text-slate-400">{item.client.email}</span>
            ) : (
              <span className="text-sm text-slate-400 italic">-</span>
            )}
          </td>
        );
      case 'client':
        return (
          <td key={col.key} className="px-4 py-3 align-top">
            {item.client ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/achat/clients/${item.client!.id}`);
                }}
                className="text-sm text-slate-700 dark:text-slate-300 hover:underline text-left"
              >
                {item.client.company_name}
              </button>
            ) : (
              <span className="text-sm text-slate-400 italic">-</span>
            )}
          </td>
        );
      case 'address':
        return (
          <td key={col.key} className="px-4 py-3 align-top text-sm max-w-xs">
            <span className={item.address ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}>
              {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') || 'Adresse non renseignée'}
            </span>
          </td>
        );
      case 'recommended_time':
        return (
          <td key={col.key} className="px-4 py-3 align-top text-sm text-slate-600 dark:text-slate-400">
            {item.recommended_time || <span className="italic text-slate-400 dark:text-slate-500">—</span>}
          </td>
        );
      case 'status':
        return (
          <td key={col.key} className="px-4 py-3 align-top text-center">
            <span className={cn(
              'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border',
              item.is_active
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800'
                : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
            )}>
              {item.is_active ? 'Actif' : 'Inactif'}
            </span>
          </td>
        );
      case 'actions':
        return (
          <td key={col.key} className="px-4 py-3 align-top text-right">
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
              className="p-2 text-red-600 bg-red-50 dark:bg-red-950/20 dark:hover:bg-red-950/30 rounded-lg transition-colors inline-flex"
              aria-label="Supprimer"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </td>
        );
      default:
        return null;
    }
  };

  const handleEdit = async (salesPoint: SalesPoint): Promise<void> => {
    setShowEditMapPreview(false);

    // Initialize form with list data first (for immediate display)
    const initialForm = {
      name: salesPoint.name,
      code: salesPoint.code,
      address: salesPoint.address,
      city: salesPoint.city,
      postal_code: salesPoint.postal_code,
      country: salesPoint.country,
      maps_link: salesPoint.maps_link,
      phone: salesPoint.phone,
      email: salesPoint.email,
      manager_name: salesPoint.manager_name,
      is_active: salesPoint.is_active,
      notes: salesPoint.notes,
      recommended_time: salesPoint.recommended_time
    };

    // Fetch fresh data from API to get all fields including recommended_time
    try {
      const response = await api.get(`/clients/sales-points/${salesPoint.id}`);
      const freshData = response.data;
      setEditForm({
        name: freshData.name,
        code: freshData.code,
        address: freshData.address,
        city: freshData.city,
        postal_code: freshData.postal_code,
        country: freshData.country,
        maps_link: freshData.maps_link,
        phone: freshData.phone,
        email: freshData.email,
        manager_name: freshData.manager_name,
        is_active: freshData.is_active,
        notes: freshData.notes,
        recommended_time: freshData.recommended_time
      });
      setEditModal(salesPoint);
    } catch (e) {
      // Fallback to list data if fetch fails
      setEditForm(initialForm);
      setEditModal(salesPoint);
    }

    // Load contacts if client exists and not already loaded
    if (salesPoint.client_id && !clientContactsById[salesPoint.client_id]) {
      try {
        setClientContactsLoadingId(salesPoint.client_id);
        const response = await api.get(`/clients/${salesPoint.client_id}/contacts`);
        const contacts: ClientContact[] = Array.isArray(response.data) ? response.data : [];
        const sorted = [...contacts].sort((a, b) => {
          const aPrimary = a.is_primary ? 1 : 0;
          const bPrimary = b.is_primary ? 1 : 0;
          return bPrimary - aPrimary;
        });
        setClientContactsById((prev) => ({ ...prev, [salesPoint.client_id]: sorted }));
      } catch (e) {
        console.error('Load contacts failed', e);
      } finally {
        setClientContactsLoadingId(null);
      }
    }
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editModal) return;

    try {
      const addressComplete = buildAddressComplete(editForm.address || '', editForm.postal_code || '', editForm.city || '');
      const payload = {
        ...editForm,
        maps_link: buildMapsLink(addressComplete) || null,
      };
      await api.patch(`/clients/sales-points/${editModal.id}`, payload);
      setEditModal(null);
      setEditForm({});
      setShowEditMapPreview(false);
      await loadSalesPoints();
      showSuccess('Point de vente mis à jour avec succès');
    } catch (e: any) {
      console.error('Update failed:', e);
      showErrorAlert(e?.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = (salesPoint: SalesPoint): void => {
    setDeleteDialog({ open: true, salesPoint });
  };

  const handleConfirmDelete = async (force: boolean = false): Promise<void> => {
    if (!deleteDialog.salesPoint) return;

    setIsDeleting(true);
    try {
      const url = force
        ? `/clients/sales-points/${deleteDialog.salesPoint.id}?force=true`
        : `/clients/sales-points/${deleteDialog.salesPoint.id}`;
      await api.delete(url);
      showSuccess('Point de vente supprimé avec succès');
      setDeleteDialog({ open: false, salesPoint: null });
      await loadSalesPoints();
    } catch (e: any) {
      console.error('Delete failed:', e);
      const errorDetail = e?.response?.data?.detail || '';

      // Check if error is about linked orders and force was not used
      if (errorDetail.includes('commande(s)') && !force) {
        // Show warning about linked orders and ask for force confirmation
        setDeleteDialog({ open: false, salesPoint: null });
        setIsDeleting(false);

        // Use a timeout to allow the dialog to close before showing alert
        setTimeout(() => {
          if (window.confirm(`${errorDetail}\n\nLes commandes seront détachées de ce point de vente mais ne seront pas supprimées.\n\nVoulez-vous continuer ?`)) {
            setDeleteDialog({ open: true, salesPoint: deleteDialog.salesPoint });
            handleConfirmDelete(true);
          }
        }, 100);
        return;
      }

      showErrorAlert(errorDetail || 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveCreate = async (): Promise<void> => {
    if (!createForm.client_id) {
      showErrorAlert('Veuillez sélectionner un client');
      return;
    }
    if (!createForm.name?.trim()) {
      showErrorAlert('Le nom est obligatoire');
      return;
    }

    try {
      const addressComplete = buildAddressComplete(createForm.address || '', createForm.postal_code || '', createForm.city || '');
      await api.post(`/clients/${createForm.client_id}/sales-points`, {
        name: createForm.name,
        code: createForm.code || null,
        address: createForm.address || null,
        city: createForm.city || null,
        postal_code: createForm.postal_code || null,
        country: createForm.country || 'France',
        maps_link: buildMapsLink(addressComplete) || null,
        phone: createForm.phone || null,
        email: createForm.email || null,
        manager_name: createForm.manager_name || null,
        is_active: createForm.is_active ?? true,
        notes: createForm.notes || null,
        recommended_time: createForm.recommended_time || null
      });
      showSuccess('Point de vente créé avec succès');
      setCreateModal(false);
      setShowCreateMapPreview(false);
      setCreateForm({
        client_id: buyerFilter !== 'all' ? buyerFilter : undefined,
        name: '',
        code: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'France',
        maps_link: '',
        phone: '',
        email: '',
        manager_name: '',
        is_active: true,
        notes: '',
        recommended_time: null
      });
      await loadSalesPoints();
    } catch (e: any) {
      console.error('Create failed:', e);
      showErrorAlert(e?.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  return (
    <div className="h-full overflow-auto md:overflow-hidden md:flex md:flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 sm:gap-4 px-3 py-2 sm:px-6 sm:py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shrink-0">
            <Store className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base sm:text-2xl font-bold text-slate-900 dark:text-slate-100 truncate">Points de vente</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
              {total} point{total > 1 ? 's' : ''} de vente{filteredSalesPoints.length !== total ? ` (${filteredSalesPoints.length} affiché${filteredSalesPoints.length > 1 ? 's' : ''})` : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setShowCreateMapPreview(false);
            setCreateModal(true);
          }}
          className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-sm bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 shrink-0"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Nouveau</span>
          <span className="sm:hidden">Nouveau</span>
        </button>
      </div>

      {/* Filters */}
      <div className="px-3 py-2 sm:px-6 sm:py-4 space-y-2 sm:space-y-4 md:flex-shrink-0">
        {/* Desktop: single row */}
        <div className="hidden md:flex gap-4 items-end">
          <div className="relative w-[60%]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Recherche (nom, code, ville, responsable...)"
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <div className="w-[20%]">
            <label htmlFor="sales_points_filter_client" className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Client</label>
            <select
              id="sales_points_filter_client"
              aria-label="Client"
              title="Client"
              value={buyerFilter}
              onChange={(e) => {
                const value = e.target.value;
                setBuyerFilter(value === 'all' ? 'all' : parseInt(value, 10));
              }}
              className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
            >
              <option value="all">Tous les client</option>
              {buyers.map((Client) => (
                <option key={Client.id} value={Client.id}>
                  {Client.company_name}
                </option>
              ))}
            </select>
          </div>
          <div className="w-[20%]">
            <label htmlFor="sales_points_filter_status" className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Statut</label>
            <select
              id="sales_points_filter_status"
              aria-label="Statut"
              title="Statut"
              value={onlyActive === 'all' ? 'all' : onlyActive ? 'true' : 'false'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'all') setOnlyActive('all');
                else setOnlyActive(value === 'true');
              }}
              className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
            >
              <option value="all">Tous les statuts</option>
              <option value="true">Actifs uniquement</option>
              <option value="false">Inactifs uniquement</option>
            </select>
          </div>
          <button
            onClick={() => setShowColumnConfig(true)}
            className="h-10 px-4 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
            title="Configurer les colonnes"
          >
            <Settings className="h-4 w-4" />
            Colonnes
          </button>
          <button
            onClick={resetAllFilters}
            className="h-10 px-4 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 text-sm text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Réinitialiser
          </button>
          <div className="ml-auto flex justify-end">
            <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                aria-label="Vue grille"
                title="Vue grille"
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-cyan-100 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <Grid3x3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                aria-label="Vue liste"
                title="Vue liste"
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-cyan-100 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                aria-label="Vue tableau"
                title="Vue tableau"
                className={`p-2 rounded ${viewMode === 'table' ? 'bg-cyan-100 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <TableIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                aria-label="Vue carte"
                title="Vue carte"
                className={`p-2 rounded ${viewMode === 'map' ? 'bg-cyan-100 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <MapIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile: compact rows */}
        <div className="flex flex-col gap-2 md:hidden">
          {/* Search */}
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Recherche (nom, code, ville...)"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          {/* Client + Status on same row */}
          <div className="flex gap-2">
            <select
              aria-label="Client"
              title="Client"
              value={buyerFilter}
              onChange={(e) => {
                const value = e.target.value;
                setBuyerFilter(value === 'all' ? 'all' : parseInt(value, 10));
              }}
              className="flex-1 min-w-0 h-9 px-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs"
            >
              <option value="all">Tous les clients</option>
              {buyers.map((Client) => (
                <option key={Client.id} value={Client.id}>
                  {Client.company_name}
                </option>
              ))}
            </select>
            <select
              aria-label="Statut"
              title="Statut"
              value={onlyActive === 'all' ? 'all' : onlyActive ? 'true' : 'false'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'all') setOnlyActive('all');
                else setOnlyActive(value === 'true');
              }}
              className="flex-1 min-w-0 h-9 px-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs"
            >
              <option value="all">Tous les statuts</option>
              <option value="true">Actifs</option>
              <option value="false">Inactifs</option>
            </select>
          </div>
          {/* Colonnes + Reset + View switcher on same row */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowColumnConfig(true)}
              className="h-8 px-2.5 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
              title="Configurer les colonnes"
            >
              <Settings className="h-3.5 w-3.5" />
              Colonnes
            </button>
            <button
              onClick={resetAllFilters}
              className="h-8 px-2.5 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 text-xs text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors flex items-center gap-1"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Réinitialiser
            </button>
            <div className="ml-auto flex items-center gap-0.5 border border-slate-300 dark:border-slate-600 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                aria-label="Vue grille"
                title="Vue grille"
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-cyan-100 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <Grid3x3 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                aria-label="Vue carte"
                title="Vue carte"
                className={`p-1.5 rounded ${viewMode === 'map' ? 'bg-cyan-100 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <MapIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
        {error && (
          <div className="p-3 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-sm text-red-700 dark:text-red-200 rounded-lg">
            {error}
          </div>
        )}
      </div>

      {/* Active Filters Tags */}
      {activeFilters.length > 0 && (
        <div className="px-6 pb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-300 font-semibold">Filtres actifs:</span>
          {activeFilters.map((tag) => (
            <span key={tag.key} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-300 shadow-sm dark:bg-cyan-950/50 dark:text-cyan-200 dark:border-cyan-700">
              {tag.label}
              <button
                type="button"
                onClick={tag.onClear}
                className="inline-flex items-center justify-center rounded-full p-0.5 text-cyan-700 hover:bg-cyan-200 hover:text-cyan-900 dark:text-cyan-200 dark:hover:bg-cyan-900/50"
                aria-label={`Supprimer ${tag.label}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Content */}
      <div className={`md:flex-1 px-3 pb-4 sm:px-6 sm:pb-6 md:min-h-0 ${viewMode === 'map' ? 'md:flex md:flex-col md:overflow-hidden' : 'md:overflow-auto'}`}>
        {(() => {
          if (showLoadingState) {
            return (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
              </div>
            );
          }

          if (showEmptyState) {
            return (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                <Store className="h-16 w-16 mb-4 opacity-50" />
                <p className="text-lg font-medium">Aucun point de vente</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Ajustez vos filtres ou réessayez plus tard.</p>
              </div>
            );
          }

          if (viewMode === 'grid') {
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5">
                {filteredSalesPoints.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleEdit(item)}
                    className="cursor-pointer group relative bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-700 hover:border-cyan-300 dark:hover:border-cyan-600 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
                  >
                    {/* Card Content */}
                    <div className="p-3 sm:p-5 flex-1">
                      {/* Header avec icône et nom */}
                      <div className="flex items-start gap-2.5 sm:gap-3 mb-3 sm:mb-4">
                        <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-cyan-100 to-emerald-100 dark:from-cyan-900/30 dark:to-emerald-900/30 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                          <Store className="h-5 w-5 sm:h-7 sm:w-7 text-cyan-600 dark:text-cyan-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-base sm:text-lg text-slate-900 dark:text-slate-100 mb-1 sm:mb-2 truncate">{item.name}</h3>
                          <div className="flex flex-wrap gap-2">
                            <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg text-xs font-medium border ${item.is_active
                              ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800'
                              : 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                              }`}>
                              {item.is_active ? '✓ Actif' : '○ Inactif'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Informations de contact */}
                      <div className="px-1 space-y-2 sm:space-y-3 mb-3 sm:mb-4">
                        <div className="flex items-start gap-3 text-sm">
                          <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <MapPin className={`h-3.5 w-3.5 ${buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`line-clamp-2 mt-1 ${item.address ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                              {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') || 'Adresse non renseignée'}
                            </span>
                            {item.country && (
                              <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">{item.country}</div>
                            )}

                            {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') && (
                              <div className="mt-2">
                                <div className="flex flex-wrap items-center gap-3">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setListMapPreviewPointId((curr) => (curr === item.id ? null : item.id));
                                    }}
                                    className={
                                      "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                      (listMapPreviewPointId === item.id
                                        ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                                    }
                                  >
                                    <MapIcon className="h-3.5 w-3.5" />
                                    Carte
                                  </button>
                                  <a
                                    href={buildMapsLink(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Ouvrir dans Google Maps
                                  </a>
                                </div>

                                {listMapPreviewPointId === item.id && (
                                  <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                    <iframe
                                      title="Google Maps"
                                      src={buildMapsEmbedSrc(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                                      className="w-full h-64"
                                      referrerPolicy="no-referrer-when-downgrade"
                                      allowFullScreen
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons - Footer */}
                    <div className="px-3 py-2 sm:px-5 sm:py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                        aria-label="Supprimer"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          }

          if (viewMode === 'list') {
            return (
              <div className="space-y-3">
                {filteredSalesPoints.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleEdit(item)}
                    className="group flex flex-col md:flex-row md:items-center gap-4 p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-cyan-300 dark:hover:border-cyan-600 hover:shadow-md transition-all duration-200 cursor-pointer"
                  >
                    {/* 1. Identity Section: Icon, Name, Status */}
                    <div className="flex items-center gap-4 w-full md:w-[35%] min-w-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-emerald-100 dark:from-cyan-900/30 dark:to-emerald-900/30 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200">
                        <Store className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate text-base mb-1.5">{item.name}</h3>
                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${item.is_active
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800'
                            : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                            }`}>
                            {item.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 2. Details Section: Client & Address */}
                    <div className="flex-1 w-full grid grid-cols-1 gap-x-6 gap-y-2 min-w-0">
                      <div className="flex items-center gap-2.5 text-sm min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                          <MapPin className={`h-4 w-4 ${buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') ? 'text-slate-400 dark:text-slate-500' : 'text-red-400 dark:text-red-500/70'}`} />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500 leading-none mb-0.5">Adresse</span>
                          <span className={`truncate ${item.address ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 italic'}`}>
                            {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') || 'Non renseignée'}
                          </span>
                          {item.country && (
                            <span className="mt-1 text-xs text-slate-500 dark:text-slate-500 truncate">{item.country}</span>
                          )}

                          {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') && (
                            <div className="mt-2">
                              <div className="flex flex-wrap items-center gap-3">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setListMapPreviewPointId((curr) => (curr === item.id ? null : item.id));
                                  }}
                                  className={
                                    "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                    (listMapPreviewPointId === item.id
                                      ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                                  }
                                >
                                  <MapIcon className="h-3.5 w-3.5" />
                                  Carte
                                </button>
                                <a
                                  href={buildMapsLink(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Ouvrir dans Google Maps
                                </a>
                              </div>

                              {listMapPreviewPointId === item.id && (
                                <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                  <iframe
                                    title="Google Maps"
                                    src={buildMapsEmbedSrc(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                                    className="w-full h-64"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    allowFullScreen
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 3. Actions Section */}
                    <div className="w-full md:w-auto flex justify-end items-center pl-4 md:border-l border-slate-100 dark:border-slate-800">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                        className="p-2 text-red-600 bg-red-50 dark:bg-red-950/20 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                        aria-label="Supprimer"
                        title="Supprimer"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          }

          if (viewMode === 'map') {
            return (
              <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden flex flex-col md:flex-1 md:min-h-0">
                  <div className="border-b border-slate-200 dark:border-slate-700 shrink-0">
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <MapIcon className="h-4 w-4 text-cyan-500" />
                        Carte des points de vente
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleRefreshMapLinks}
                          disabled={mapRefreshBusy || mapSummary.withoutCoords.length === 0}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Mettre à jour les coordonnées manquantes"
                        >
                          {mapRefreshBusy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                          )}
                          {mapRefreshBusy ? 'Géolocalisation...' : `Géolocaliser (${mapSummary.withoutCoords.length})`}
                        </button>
                      </div>
                    </div>
                    {geocodeProgress && (
                      <div className="px-4 pb-3 space-y-1.5">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-medium text-emerald-700 dark:text-emerald-300">
                            {mapRefreshBusy ? (
                              <span className="flex items-center gap-1.5">
                                <span className="relative flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                </span>
                                Géolocalisation en cours...
                              </span>
                            ) : (
                              'Terminé !'
                            )}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                            {geocodeProgress.done}/{geocodeProgress.total} traités — {geocodeProgress.updated} localisé{geocodeProgress.updated > 1 ? 's' : ''}
                            {geocodeProgress.failed > 0 && `, ${geocodeProgress.failed} échoué${geocodeProgress.failed > 1 ? 's' : ''}`}
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                          {mapRefreshBusy && geocodeProgress.done === 0 ? (
                            <div className="h-full w-full rounded-full bg-gradient-to-r from-emerald-500/30 via-emerald-500 to-emerald-500/30 animate-pulse" />
                          ) : (
                            <div
                              className="h-full rounded-full transition-all duration-700 ease-out bg-gradient-to-r from-emerald-500 to-emerald-400"
                              style={{ width: `${geocodeProgress.total > 0 ? Math.max(2, (geocodeProgress.done / geocodeProgress.total) * 100) : 0}%` }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="relative h-[60vh] md:h-auto md:flex-1 md:min-h-0">
                    <div ref={mapRef} className="absolute inset-0 z-0" />
                    {(mapLoading || busy) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-slate-900/70">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-200">
                          <Loader2 className="h-5 w-5 text-cyan-500 animate-spin" />
                          {busy ? 'Chargement des points de vente...' : 'Chargement des points...'}
                        </div>
                      </div>
                    )}
                  </div>
              </div>
            );
          }

          // Table view (default fallback)
          return (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg h-full flex flex-col min-h-0">
              <div className="flex-1 overflow-auto min-h-0">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 border-b-2 border-cyan-200 dark:border-cyan-800">
                    <tr>
                      {visibleColumns.map((col) => renderTableHeader(col))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {filteredSalesPoints.map((item) => (
                      <tr
                        key={item.id}
                        onClick={() => void handleEdit(item)}
                        className="hover:bg-cyan-50/60 dark:hover:bg-cyan-950/20 transition-colors duration-200 cursor-pointer"
                      >
                        {visibleColumns.map((col) => renderTableCell(col, item))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Pagination Controls */}
        {total > pageSize && viewMode !== 'map' && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-3 sm:px-4 sm:py-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm mt-4">
            <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 text-center sm:text-left whitespace-nowrap">
              <span className="font-semibold text-slate-700 dark:text-slate-200">{(page - 1) * pageSize + 1}</span>–<span className="font-semibold text-slate-700 dark:text-slate-200">{Math.min(page * pageSize, total)}</span> sur <span className="font-semibold text-slate-700 dark:text-slate-200">{total}</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Préc.
              </button>
              <div className="flex items-center gap-0.5 sm:gap-1">
                {Math.min(5, Math.ceil(total / pageSize)) > 1 && Array.from({ length: Math.ceil(total / pageSize) }, (_, i) => i + 1)
                  .filter(pageNum => {
                    const totalPages = Math.ceil(total / pageSize);
                    if (totalPages <= 5) return true;
                    return pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= page - 1 && pageNum <= page + 1);
                  })
                  .map((pageNum, i, arr) => {
                    return (
                      <React.Fragment key={pageNum}>
                        {i > 0 && arr[i - 1] !== pageNum - 1 && (
                          <span className="px-1 text-slate-400 text-xs">…</span>
                        )}
                        <button
                          onClick={() => setPage(pageNum)}
                          className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-xs sm:text-sm font-medium rounded-lg transition-colors ${page === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                        >
                          {pageNum}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>
              <button
                onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))}
                disabled={page === Math.ceil(total / pageSize)}
                className="px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Suiv.
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de visualisation */}
      {viewModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-4 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white">Détails du point de vente</h2>
            </div>

            <div className="p-6 space-y-6">
              {/* En-tête avec Statut */}
              <div className="flex flex-col md:flex-row justify-between items-start gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{viewModal.name}</h3>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${viewModal.is_active
                  ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
                  : 'bg-gray-50 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                  }`}>
                  {viewModal.is_active ? '● Actif' : '○ Inactif'}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Colonne Gauche : Infos & Contact */}
                <div className="space-y-6">
                  {/* Informations */}
                  <div>
                    <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
                      <Store className="h-4 w-4 text-cyan-500" />
                      Informations
                    </h4>
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
                    </div>
                  </div>
                </div>

                {/* Colonne Droite : Adresse & Notes */}
                <div className="space-y-6">
                  {/* Adresse */}
                  <div>
                    <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
                      <MapPin className="h-4 w-4 text-cyan-500" />
                      Localisation
                    </h4>
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                      {buildAddressComplete(viewModal.address || '', viewModal.postal_code || '', viewModal.city || '') ? (
                        <div className="flex gap-3">
                          <MapPin className="h-5 w-5 text-emerald-500 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                          <div className="space-y-1">
                            <div className="font-medium text-slate-900 dark:text-slate-100">
                              {buildAddressComplete(viewModal.address || '', viewModal.postal_code || '', viewModal.city || '')}
                            </div>
                            {viewModal.country && (
                              <div className="text-slate-500 dark:text-slate-400 font-medium">
                                {viewModal.country}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <MapPin className="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-slate-400 italic pt-0.5">Adresse non renseignée</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  {viewModal.notes && (
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
                        Notes
                      </h4>
                      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-200">
                        {viewModal.notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Dates */}
              <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-400">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Créé le {new Date(viewModal.created_at).toLocaleString('fr-FR')}
                </div>
                <div className="flex items-center gap-1.5">
                  <Edit className="h-3.5 w-3.5" />
                  Dernière modification le {new Date(viewModal.updated_at).toLocaleString('fr-FR')}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-800 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setViewModal(null)}
                className="px-4 py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de création */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3.5 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold text-white">Nouveau point de vente</h2>
              <button
                onClick={() => {
                  setShowCreateMapPreview(false);
                  setCreateModal(false);
                }}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Fermer"
                title="Fermer"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="sales_point_create_client" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Client *</label>
                  <select
                    id="sales_point_create_client"
                    aria-label="Client"
                    title="Client"
                    value={createForm.client_id || ''}
                    onChange={(e) => {
                      const selectedClientId = parseInt(e.target.value, 10);
                      const selectedBuyer = buyers.find((b) => b.id === selectedClientId);
                      setCreateForm({
                        ...createForm,
                        client_id: selectedClientId,
                        name: `${selectedBuyer?.first_name ?? ''} ${selectedBuyer?.last_name ?? ''}`.trim()
                      });
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  >
                    <option value="">Sélectionner un client...</option>
                    {buyers.map((buyer) => (
                      <option key={buyer.id} value={buyer.id}>
                        {buyer.company_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="sales_point_create_name" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Nom *</label>
                  <input
                    id="sales_point_create_name"
                    type="text"
                    value={createForm.name || ''}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Ex: Magasin Paris Centre"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="border-2 border-cyan-300 dark:border-cyan-600 p-3.5 rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30">
                <label className="block text-sm font-semibold mb-2 text-cyan-700 dark:text-cyan-300 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Adresse
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="sales_point_create_address" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rue</label>
                    <input
                      id="sales_point_create_address"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      value={createForm.address || ''}
                      onChange={(e) =>
                        setCreateForm((prev) => {
                          const next = { ...prev, address: e.target.value };
                          const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                          return { ...next, maps_link: buildMapsLink(addressComplete) };
                        })
                      }
                      placeholder="Ex: 44 Avenue Gambetta"
                    />
                  </div>
                  <div>
                    <label htmlFor="sales_point_create_postal_code" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Code postal</label>
                    <input
                      id="sales_point_create_postal_code"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      value={createForm.postal_code || ''}
                      onChange={(e) =>
                        setCreateForm((prev) => {
                          const next = { ...prev, postal_code: e.target.value };
                          const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                          return { ...next, maps_link: buildMapsLink(addressComplete) };
                        })
                      }
                      placeholder="Ex: 17100"
                    />
                  </div>
                  <div>
                    <label htmlFor="sales_point_create_city" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Ville</label>
                    <input
                      id="sales_point_create_city"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      value={createForm.city || ''}
                      onChange={(e) =>
                        setCreateForm((prev) => {
                          const next = { ...prev, city: e.target.value };
                          const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                          return { ...next, maps_link: buildMapsLink(addressComplete) };
                        })
                      }
                      placeholder="Ex: Saintes"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="sales_point_create_address_complete" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Adresse complète</label>
                    <input
                      id="sales_point_create_address_complete"
                      className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                      value={buildAddressComplete(createForm.address || '', createForm.postal_code || '', createForm.city || '')}
                      readOnly
                    />

                    {buildAddressComplete(createForm.address || '', createForm.postal_code || '', createForm.city || '') && (
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setShowCreateMapPreview((v) => !v)}
                          className={
                            "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                            (showCreateMapPreview
                              ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                          }
                        >
                          <MapIcon className="h-3.5 w-3.5" />
                          Carte
                        </button>
                        {createForm.maps_link && (
                          <a
                            href={String(createForm.maps_link)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            Ouvrir dans Google Maps
                          </a>
                        )}
                      </div>
                    )}

                    {showCreateMapPreview && (
                      <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                        <iframe
                          title="Google Maps"
                          src={buildMapsEmbedSrc(buildAddressComplete(createForm.address || '', createForm.postal_code || '', createForm.city || ''))}
                          className="w-full h-64"
                          referrerPolicy="no-referrer-when-downgrade"
                          allowFullScreen
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="sales_point_create_recommended_time" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Horaire recommandé</label>
                <select
                  id="sales_point_create_recommended_time"
                  aria-label="Horaire recommandé"
                  title="Horaire recommandé"
                  value={createForm.recommended_time || ''}
                  onChange={(e) => setCreateForm({ ...createForm, recommended_time: e.target.value || null })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                >
                  <option value="">— Sélectionner —</option>
                  {TIME_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Notes</label>
                <textarea
                  value={createForm.notes || ''}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  rows={2}
                  placeholder="Informations complémentaires..."
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all resize-none"
                />
              </div>

              <div>
                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={createForm.is_active ?? true}
                    onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">Point de vente actif</span>
                </label>
              </div>
            </div>

            <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 px-5 py-3.5 rounded-b-2xl flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700 shadow-lg flex-shrink-0">
              <button
                onClick={() => setCreateModal(false)}
                className="px-4 py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 hover:shadow-md transition-all font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveCreate}
                disabled={!createForm.client_id || !createForm.name?.trim()}
                className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-lg shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed disabled:opacity-60 font-semibold"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">Modification du point de vente: {editModal.name}</h2>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label htmlFor="sales_point_edit_name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nom *</label>
                <input
                  id="sales_point_edit_name"
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="border border-blue-300 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                <label className="block text-sm font-medium mb-2 text-blue-700 dark:text-blue-300">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Adresse
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="sales_point_edit_address" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rue</label>
                    <input
                      id="sales_point_edit_address"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editForm.address || ''}
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const next = { ...prev, address: e.target.value };
                          const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                          return { ...next, maps_link: buildMapsLink(addressComplete) };
                        })
                      }
                      placeholder="Ex: 44 Avenue Gambetta"
                    />
                  </div>
                  <div>
                    <label htmlFor="sales_point_edit_postal_code" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Code postal</label>
                    <input
                      id="sales_point_edit_postal_code"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editForm.postal_code || ''}
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const next = { ...prev, postal_code: e.target.value };
                          const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                          return { ...next, maps_link: buildMapsLink(addressComplete) };
                        })
                      }
                      placeholder="Ex: 17100"
                    />
                  </div>
                  <div>
                    <label htmlFor="sales_point_edit_city" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Ville</label>
                    <input
                      id="sales_point_edit_city"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editForm.city || ''}
                      onChange={(e) =>
                        setEditForm((prev) => {
                          const next = { ...prev, city: e.target.value };
                          const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                          return { ...next, maps_link: buildMapsLink(addressComplete) };
                        })
                      }
                      placeholder="Ex: Saintes"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="sales_point_edit_address_complete" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Adresse complète</label>
                    <input
                      id="sales_point_edit_address_complete"
                      className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                      value={buildAddressComplete(editForm.address || '', editForm.postal_code || '', editForm.city || '')}
                      readOnly
                    />

                    {buildAddressComplete(editForm.address || '', editForm.postal_code || '', editForm.city || '') && (
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setShowEditMapPreview((v) => !v)}
                          className={
                            "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                            (showEditMapPreview
                              ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                          }
                        >
                          <MapIcon className="h-3.5 w-3.5" />
                          Carte
                        </button>
                        {editForm.maps_link && (
                          <a
                            href={String(editForm.maps_link)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            Ouvrir dans Google Maps
                          </a>
                        )}
                      </div>
                    )}

                    {showEditMapPreview && (
                      <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                        <iframe
                          title="Google Maps"
                          src={buildMapsEmbedSrc(buildAddressComplete(editForm.address || '', editForm.postal_code || '', editForm.city || ''))}
                          className="w-full h-64"
                          referrerPolicy="no-referrer-when-downgrade"
                          allowFullScreen
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>


              {/* Client Contacts Section */}
              {editModal.client && (
                <div className="border-2 border-purple-300 p-4 rounded-lg bg-purple-50 dark:bg-purple-950/20">
                  <h3 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-3 flex items-center gap-2">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Contacts du client: {editModal.client.company_name}
                  </h3>
                  {clientContactsLoadingId === editModal.client_id ? (
                    <div className="text-sm text-slate-500 dark:text-slate-400">Chargement des contacts...</div>
                  ) : (
                    (() => {
                      const contacts = clientContactsById[editModal.client_id] ?? [];
                      if (!contacts.length) {
                        return (
                          <div className="text-sm text-slate-400 italic">Aucun contact disponible</div>
                        );
                      }
                      return (
                        <div className="space-y-2">
                          {contacts.map((contact) => (
                            <div key={contact.id} className="border border-purple-200 dark:border-purple-700 rounded-lg p-3 bg-white/80 dark:bg-slate-900/40">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {getContactDisplayName(contact)}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (editModal.client) navigate(`/achat/clients/${editModal.client.id}`);
                                    }}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                    title="Voir les informations du client"
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                {contact.is_primary && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Principal</span>
                                )}
                              </div>
                              {contact.email && (
                                <div className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                                  <a href={`mailto:${contact.email}`} className="hover:underline">{contact.email}</a>
                                </div>
                              )}
                              {contact.phone && (
                                <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                                  <a href={`tel:${contact.phone}`} className="hover:underline">{contact.phone}</a>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  )}
                </div>
              )}

              <div>
                <label htmlFor="sales_point_edit_recommended_time" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Horaire recommandé</label>
                <select
                  id="sales_point_edit_recommended_time"
                  value={editForm.recommended_time || ''}
                  onChange={(e) => setEditForm({ ...editForm, recommended_time: e.target.value || null })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Sélectionner —</option>
                  {editForm.recommended_time && !TIME_SLOTS.includes(editForm.recommended_time) && (
                    <option value={editForm.recommended_time}>{editForm.recommended_time}</option>
                  )}
                  {TIME_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="sales_point_edit_notes" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
                <textarea
                  id="sales_point_edit_notes"
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.is_active ?? true}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Point de vente actif</span>
                </label>
              </div>
            </div>

            <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-800 px-4 py-3 rounded-b-2xl flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => {
                  setEditModal(null);
                  setEditForm({});
                  setShowEditMapPreview(false);
                }}
                className="px-4 py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, salesPoint: open ? deleteDialog.salesPoint : null })}
        title={
          deleteDialog.salesPoint && salesPoints.filter(sp => sp.client_id === deleteDialog.salesPoint?.client_id).length === 1
            ? '⚠️ Attention : Dernier point de vente'
            : 'Confirmer la suppression'
        }
        description={
          deleteDialog.salesPoint && salesPoints.filter(sp => sp.client_id === deleteDialog.salesPoint?.client_id).length === 1
            ? `Vous êtes sur le point de supprimer le dernier point de vente "${deleteDialog.salesPoint?.name}" de ce client (${deleteDialog.salesPoint?.client?.company_name || 'Client'}). Le client n'aura plus aucun point de vente après cette suppression. Êtes-vous sûr de vouloir continuer ?`
            : `Êtes-vous sûr de vouloir supprimer le point de vente "${deleteDialog.salesPoint?.name}" ? Cette action est irréversible.`
        }
        confirmText={
          deleteDialog.salesPoint && salesPoints.filter(sp => sp.client_id === deleteDialog.salesPoint?.client_id).length === 1
            ? 'Supprimer quand même'
            : 'Supprimer'
        }
        cancelText="Annuler"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />

      {/* Column Configurator */}
      <ColumnConfigurator
        isOpen={showColumnConfig}
        onClose={() => setShowColumnConfig(false)}
        columns={columns}
        onChange={setColumns}
        storageKey={columnStorageKey}
      />
    </div>
  );
};

export default SalesPointsTailwind;
